import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiZap, FiTrendingDown, FiShield, FiBarChart2, FiUsers,
  FiArrowRight, FiCheck, FiPhone, FiMail, FiClock, FiStar
} from 'react-icons/fi';

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-9 h-9 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">AQ</span>
            </div>
            <span className="font-semibold text-gray-900 text-lg">AIQCON Energy</span>
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm">
            <a href="#vorteile" className="text-gray-600 hover:text-gray-900">Vorteile</a>
            <a href="#so-funktioniert-es" className="text-gray-600 hover:text-gray-900">So funktioniert es</a>
            <a href="#kontakt" className="text-gray-600 hover:text-gray-900">Kontakt</a>
          </div>
          <Link
            to="/login"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 border border-primary-200 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors"
          >
            Team-Login
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center space-x-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FiZap size={16} />
              <span>Ihr Partner fuer guenstigeren Gewerbestrom</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight max-w-4xl mx-auto">
              Stromkosten senken.
              <br />
              <span className="text-primary-600">Intelligent und transparent.</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Wir vergleichen die besten Energieversorger fuer Ihr Unternehmen und finden den optimalen Tarif —
              damit Sie sich auf Ihr Kerngeschaeft konzentrieren koennen.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <a
                href="#kontakt"
                className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-primary-600 text-white px-8 py-3.5 rounded-xl font-medium hover:bg-primary-700 transition-colors text-lg"
              >
                <span>Kostenlose Beratung</span>
                <FiArrowRight size={20} />
              </a>
              <a
                href="tel:+4917012345678"
                className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 border-2 border-gray-200 text-gray-700 px-8 py-3.5 rounded-xl font-medium hover:border-gray-300 hover:bg-gray-50 transition-colors text-lg"
              >
                <FiPhone size={18} />
                <span>Jetzt anrufen</span>
              </a>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div {...fadeUp} transition={{ delay: 0.3 }} className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {[
              { value: '15%', label: 'durchschnittliche Ersparnis' },
              { value: '500+', label: 'zufriedene Kunden' },
              { value: '10+', label: 'Energieversorger' },
              { value: '24h', label: 'Angebotserstellung' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-bold text-primary-600">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Vorteile */}
      <section id="vorteile" className="py-20 bg-gray-50 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Warum Unternehmen uns vertrauen</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Wir uebernehmen den kompletten Beschaffungsprozess — Sie profitieren von besseren Preisen ohne Aufwand.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: FiTrendingDown,
                color: 'bg-green-100 text-green-600',
                title: 'Bis zu 20% guenstigere Strompreise',
                desc: 'Wir verhandeln direkt mit den Versorgern und nutzen unsere Einkaufsmacht fuer bessere Konditionen als Sie alleine erhalten wuerden.'
              },
              {
                icon: FiShield,
                color: 'bg-blue-100 text-blue-600',
                title: 'Volle Transparenz, keine versteckten Kosten',
                desc: 'Sie sehen genau was Sie zahlen. Ein Preis pro kWh — fertig. Keine Grundgebuehren, keine Ueberraschungen.'
              },
              {
                icon: FiBarChart2,
                color: 'bg-purple-100 text-purple-600',
                title: 'Datenbasierte Optimierung',
                desc: 'Wir analysieren Ihren Lastgang und finden den Tarif, der exakt zu Ihrem Verbrauchsprofil passt — nicht von der Stange.'
              },
              {
                icon: FiClock,
                color: 'bg-amber-100 text-amber-600',
                title: 'Angebot in 24 Stunden',
                desc: 'Nach unserem Erstgespraech erhalten Sie innerhalb eines Werktags ein massgeschneidertes Angebot mit mehreren Optionen.'
              },
              {
                icon: FiUsers,
                color: 'bg-indigo-100 text-indigo-600',
                title: 'Persoenlicher Ansprechpartner',
                desc: 'Kein Callcenter, kein Bot. Ihr fester Berater kennt Ihr Unternehmen und ist jederzeit erreichbar.'
              },
              {
                icon: FiStar,
                color: 'bg-rose-100 text-rose-600',
                title: 'Kuendigungsservice inklusive',
                desc: 'Mit Ihrer Vollmacht kuendigen wir den alten Vertrag und kuemmern uns um den gesamten Wechselprozess.'
              },
            ].map((item, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <div className="bg-white rounded-2xl p-8 h-full border border-gray-100 hover:shadow-lg transition-shadow">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${item.color} mb-5`}>
                    <item.icon size={24} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* So funktioniert es */}
      <section id="so-funktioniert-es" className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">So einfach funktioniert es</h2>
            <p className="mt-4 text-lg text-gray-600">In drei Schritten zu Ihrem optimalen Stromtarif</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                step: '1',
                title: 'Kostenlose Erstberatung',
                desc: 'Wir besprechen Ihren aktuellen Verbrauch, Vertrag und Ihre Anforderungen. Dauer: ca. 15 Minuten.',
                detail: 'Jahresverbrauch, PLZ, aktuelle Konditionen'
              },
              {
                step: '2',
                title: 'Massgeschneidertes Angebot',
                desc: 'Sie erhalten einen persoenlichen Link mit mehreren Preisoptionen zum direkten Vergleich.',
                detail: 'Transparente Preise, Ersparnis auf einen Blick'
              },
              {
                step: '3',
                title: 'Wechsel ohne Aufwand',
                desc: 'Sie waehlen die beste Option — wir kuemmern uns um Kuendigung, Vertrag und Umstellung.',
                detail: 'Vollmacht genuegt, wir erledigen den Rest'
              },
            ].map((item, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.15 }} className="text-center">
                <div className="w-14 h-14 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <span className="text-white text-2xl font-bold">{item.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 mb-4">{item.desc}</p>
                <p className="text-sm text-primary-600 font-medium">{item.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fuer wen */}
      <section className="py-20 bg-primary-600 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Fuer wen ist AIQCON Energy?</h2>
            <p className="text-primary-100 text-lg max-w-2xl mx-auto mb-12">
              Wir arbeiten mit Unternehmen ab 50.000 kWh Jahresverbrauch — vom Handwerksbetrieb bis zur Industrieanlage.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {['Produktion & Fertigung', 'Metallverarbeitung', 'Lebensmittelindustrie', 'Kunststoffverarbeitung', 'Lagerhallen & Logistik', 'Hotels & Gastronomie', 'Buerogebaeude', 'Einzelhandel'].map((industry, i) => (
                <div key={i} className="flex items-center space-x-2 text-left">
                  <FiCheck className="text-primary-200 flex-shrink-0" size={16} />
                  <span className="text-white text-sm">{industry}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Kontakt */}
      <section id="kontakt" className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div {...fadeUp}>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Lassen Sie uns sprechen</h2>
              <p className="mt-4 text-lg text-gray-600">
                Kontaktieren Sie uns fuer eine kostenlose und unverbindliche Erstberatung.
                Wir melden uns innerhalb von 24 Stunden.
              </p>
              <div className="mt-8 space-y-4">
                <a href="tel:+4917012345678" className="flex items-center space-x-3 text-gray-700 hover:text-primary-600">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <FiPhone className="text-primary-600" size={18} />
                  </div>
                  <div>
                    <p className="font-medium">+49 170 123 456 78</p>
                    <p className="text-sm text-gray-500">Mo-Fr, 8:00 - 18:00 Uhr</p>
                  </div>
                </a>
                <a href="mailto:kontakt@aiqcon.com" className="flex items-center space-x-3 text-gray-700 hover:text-primary-600">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <FiMail className="text-primary-600" size={18} />
                  </div>
                  <div>
                    <p className="font-medium">kontakt@aiqcon.com</p>
                    <p className="text-sm text-gray-500">Antwort innerhalb von 24h</p>
                  </div>
                </a>
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ delay: 0.2 }}>
              <form className="bg-gray-50 rounded-2xl p-8 space-y-5" onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vorname</label>
                    <input className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" placeholder="Max" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nachname</label>
                    <input className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" placeholder="Mustermann" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Firma</label>
                  <input className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" placeholder="Mustermann GmbH" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                    <input type="email" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" placeholder="max@firma.de" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                    <input className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" placeholder="+49 ..." />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jahresverbrauch (ca.)</label>
                  <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none">
                    <option>50.000 - 100.000 kWh</option>
                    <option>100.000 - 250.000 kWh</option>
                    <option>250.000 - 500.000 kWh</option>
                    <option>500.000 - 1.000.000 kWh</option>
                    <option>Ueber 1.000.000 kWh</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nachricht (optional)</label>
                  <textarea className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none h-20 resize-none" placeholder="Wie koennen wir Ihnen helfen?" />
                </div>
                <button
                  type="submit"
                  className="w-full bg-primary-600 text-white py-3 rounded-xl font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Kostenlos beraten lassen</span>
                  <FiArrowRight size={18} />
                </button>
                <p className="text-xs text-gray-400 text-center">Unverbindlich und kostenfrei. Ihre Daten werden vertraulich behandelt.</p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">AQ</span>
              </div>
              <span className="font-semibold text-white">AIQCON Energy</span>
            </div>
            <div className="flex items-center space-x-6 text-sm">
              <span>Energieberatung &amp; Optimierung</span>
              <span>&middot;</span>
              <a href="mailto:kontakt@aiqcon.com" className="hover:text-white">kontakt@aiqcon.com</a>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-xs">
            <p>&copy; {new Date().getFullYear()} AIQCON GmbH. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
